import React, { useEffect } from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import InKindPageBackground from '@gik/create/components/InKindPageBackground/InKindPageBackground';
import Modal from '@gik/ui/Modal';
import {
  Button,
  CenterFixed,
  Drawer,
  FormType,
  HTMLParser,
  IFormSubmitAttemptOptions,
  LoadingSpinner,
  StepItem,
  Steps,
  SubStep,
  SvgIcon,
  UI,
} from '@gik/ui';
import AboutStep, { legalDrawerLoading } from '@gik/create/components/AboutStep/AboutStep';
import SituationStep, { selectedCategoryStorageKey } from '@gik/create/components/SituationStep/SituationStep';
import PrivacyStep from '@gik/create/components/PrivacyStep/PrivacyStep';
import FinishStep from '@gik/create/components/FinishStep/FinishStep';
import { isBrowser, navigateTo } from '@/utils/environment';
import i18n from '@gik/i18n';
import { translationKeys } from '@gik/create/i18n/en';
import CreatePageNavigation from '@gik/create/components/CreatePageNavigation/CreatePageNavigation';
import CareCalendarStep from '@gik/create/components/CareCalendarStep/CareCalendarStep';
import WishlistStep from '@gik/create/components/WishlistStep/WishlistStep';
import DonationsStep from '@gik/create/components/DonationsStep/DonationsStep';
import NameYourPageStep from '@gik/create/components/PageStep/NameYourPageStep';
import RecipientInfoStep from '@gik/create/components/RecipientInfoStep/RecipientInfoStep';
import FeaturesAside from '@gik/create/components/FeaturesAside/FeaturesAside';
import { useCreateFlowStore } from '@gik/create/store/CreateFlowStore';
import { ICreateFlowFormValues } from '@gik/create/models/CreateFlowFormValues';
import { ICreatePageContext } from '@gik/create/interfaces/ICreatePageContext';
import shallow from 'zustand/shallow';
import { useSuggestedProducts } from '@gik/checkout';
import { useInkindSituations } from '@gik/marketing/api/wp/situation';
import { useEffectOnce, useLocalStorage } from 'react-use';
import CreatePageSplitLayout from '@gik/create/components/CreatePageSplitLayout/CreatePageSplitLayout';
import { useEmailAlreadyTaken } from '@gik/api/users/auth';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { RecipientType } from '@gik/create/enums/RecipientType';
import { userStoreApi, useUserStore } from '@gik/core/store/UserStore';
import { useFeatureVideos } from '@gik/api/acf/gik-settings/featureVideos';
import { createInkind } from '@gik/api/inkinds/inkinds';
import { getUser } from '@gik/api/users/user';
import { User } from '@gik/core/models/gik/User';
import useCreateAnalytics from '@gik/create/hooks/useCreateAnalytics';
import { IWordpressCreateFlowCategory } from '@gik/core/models/wordpress/WordpressSituation';
import { emailUserTags, removeEmailUserTags } from '@gik/api/emailUsers/emailUsers';
import PageNameIdeas from '../PageStep/PageNameIdeas';
import Slider from 'react-slick';
import { AffiliateTracking, Analytics } from '@gik/core/utils';
import { useTranslation } from 'react-i18next';
import { useUserPages } from '@gik/api/inkinds/userPages';
import { cookies } from '@gik/core/utils/Cookies';
import { authCookieKeys } from '@gik/auth/utils/auth';
import OAuthErrorModalContent from '@gik/auth/components/Login/OAuthErrorModalContent';
import sleep from '@gik/core/utils/sleep';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { extractCampaignSlug } from '@gik/core/utils/GoFundMe';
import { CreateFormSteps } from '@gik/create/enums/CreateFormSteps';
import { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';
import { EmailTakenStatus } from '@gik/create/enums/EmailTakenStatus';
import { FeatureWishlistInputNames } from '@gik/create/enums/FeatureWishlistInputNames';
import { FeatureCareCalendarInputNames } from '@gik/create/enums/FeatureCareCalendarInputNames';
import { FeatureDonationsInputNames } from '@gik/create/enums/FeatureDonationsInputNames';
import { PayPalFormInputNames } from '@gik/create/enums/PayPalFormInputNames';
import { GoFundMeFormInputNames } from '@gik/create/enums/GoFundMeFormInputNames';
import { RecipientInfoStepInputNames } from '@gik/create/enums/RecipientInfoStepInputNames';
import ReactDOM from 'react-dom';
import { CreatingPageIndicator } from './CreatingPageIndicator';

const blockName = 'create-page';

// we use a context to store refs instead of zustand since context changes do not trigger a redraw
export const CreatePageContext = React.createContext<ICreatePageContext>(null);

export const stepIndexStorageKey = 'gik-create-form-step-index';
export const subStepIndexStorageKey = 'gik-create-form-sub-step-index';
// TODO: remove after SPA migration - redirects will not interrupt events
export const createPageFinishedKey = 'gik-create-finished';

export enum CreatePageFormIds {
  WishListStepForm = 'WishListStepForm',
  CareCalendarStepForm = 'CareCalendarStepForm',
  DonationsStepForm = 'DonationsStepForm',
}

export function CreatePageFallback() {
  const isMd = useBreakpoint(Breakpoint.MD);
  const createProgress = useCreateFlowStore(store => store.createProgress);

  return (
    <>
      <InKindPageBackground />
      {ReactDOM.createPortal(
        <CenterFixed>
          {createProgress > 0 ? (
            <CreatingPageIndicator progress={createProgress} />
          ) : (
            <LoadingSpinner variant={isMd ? 'primary' : 'default'} />
          )}
        </CenterFixed>,
        document.body
      )}
    </>
  );
}

export default withSuspense(function CreatePage() {
  const bem = bemBlock(blockName);
  const { t } = useTranslation();
  const drawerBem = bemBlock('create-page-drawer');
  const pageNameBem = bemBlock('create-page-sheet-page-name-ideas');

  const userStore = useUserStore();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [blurred, setBlurred] = React.useState<boolean>(false);

  const updateCreateProgress = useCreateFlowStore(store => store.updateCreateProgress);
  const situationCarouselRef = useCreateFlowStore(state => state.situationCarouselRef);

  const { formValues, setFormValues } = useCreateFlowStore(
    state => ({
      formValues: state.formValues,
      setFormValues: state.setFormValues,
      nextDisabled: state.nextDisabled,
    }),
    shallow
  );

  const steps: StepItem<CreateFormSteps>[] = [
    {
      prepend: <SvgIcon name="UserCircle" />,
      name: i18n.t(translationKeys.AboutTitle),
      id: CreateFormSteps.ABOUT,
    },
    {
      prepend: <SvgIcon name="Heart" />,
      name: i18n.t(translationKeys.SituationTitle),
      id: CreateFormSteps.SITUATION,
    },
    {
      prepend: <SvgIcon name="DocumentText" />,
      name: i18n.t(translationKeys.PageTitle),
      id: CreateFormSteps.PAGE,
      subSteps: formValues.recipientType != RecipientType.Myself ? [{}, {}] : undefined,
    },
    {
      prepend: <SvgIcon name="Sparkles" />,
      name: i18n.t(translationKeys.FeaturesTitle),
      id: CreateFormSteps.FEATURES,
      subSteps: [{}, {}, {}],
    },
    {
      prepend: <SvgIcon name="EyeOff" />,
      name: i18n.t(translationKeys.PrivacyTitle),
      id: CreateFormSteps.PRIVACY,
    },
  ];

  if (!userStore.id) {
    // only need the "finish" step if the user is not logged in
    steps.push({
      prepend: <SvgIcon name="ClipboardCheck" />,
      name: i18n.t(translationKeys.FinishTitle),
      id: CreateFormSteps.FINISH,
    });
  }

  const {
    trackIntercomClicked,
    trackCalendarVideoPlayed,
    trackWishlistVideoPlayed,
    trackDonationsVideoPlayed,
    trackOnFinish,
    trackClickedPageNameIdeas,
    trackTermsOfService,
    trackPrivacyPolicy,
  } = useCreateAnalytics(steps);

  const setNextDisabled = useCreateFlowStore(state => state.setNextDisabled);
  const {
    stepIndex,
    setStepIndex,
    subStepIndex,
    setSubStepIndex,
    setSuggestedProducts,
    setSituations,
    setSituationCategories,
    setEmailTakenStatus,
    resetFormValues,
    legalTextDrawerTitle,
    legalTextDrawerCopy,
    closeLegalTextDrawer,
    isPageNameDrawerOpen,
    closePageNameDrawer,
  } = useCreateFlowStore(
    state => ({
      stepIndex: state.stepIndex,
      setStepIndex: state.setStepIndex,
      subStepIndex: state.subStepIndex,
      setSubStepIndex: state.setSubStepIndex,
      setSuggestedProducts: state.setSuggestedProducts,
      setSituations: state.setSituations,
      setSituationCategories: state.setSituationCategories,
      setEmailTakenStatus: state.setEmailTakenStatus,
      resetFormValues: state.resetFormValues,
      legalTextDrawerTitle: state.legalTextDrawerTitle,
      legalTextDrawerCopy: state.legalTextDrawerCopy,
      closeLegalTextDrawer: state.closeLegalTextDrawer,
      isPageNameDrawerOpen: state.isPageNameDrawerOpen,
      closePageNameDrawer: state.closePageNameDrawer,
    }),
    shallow
  );

  const isMd = useBreakpoint(Breakpoint.MD);
  const { revalidate: revalidateUserPages } = useUserPages(userStore.id);

  const isDrawerOpen = legalTextDrawerTitle !== null && legalTextDrawerCopy !== null;
  const isDrawerLoading = legalTextDrawerCopy === legalDrawerLoading;
  const selectedCategoryIndex = useCreateFlowStore(state => state.selectedCategoryIndex);
  const situationCategories = useCreateFlowStore(state => state.situationCategories);
  const setNewUserLoginProvider = useCreateFlowStore(state => state.setNewUserLoginProvider);

  function getSituationCategories() {
    let allSituationCategories: IWordpressCreateFlowCategory[] = [];

    situations.forEach(situation => {
      if (!situation.acf.create_flow_categories) {
        return;
      }
      allSituationCategories = allSituationCategories.concat(situation.acf.create_flow_categories);
    });

    return allSituationCategories
      .reduce((unique: IWordpressCreateFlowCategory[], item: IWordpressCreateFlowCategory) => {
        return unique.some(e => e.value === item.value) ? unique : [...unique, item];
      }, [])
      .sort((a, b) => a.value.localeCompare(b.value)); // sort by values (format = weighting-taxonomyname)
  }

  const { data: suggestedProducts } = useSuggestedProducts(formValues.situations);
  React.useEffect(() => {
    setSuggestedProducts?.(suggestedProducts);
  }, [suggestedProducts]);

  const { data: situations } = useInkindSituations(1, 100);
  React.useEffect(() => {
    if (!situations) {
      return;
    }
    setSituations?.(situations);
    setSituationCategories(getSituationCategories());
  }, [situations]);

  const { data: emailTakenStatus } = useEmailAlreadyTaken(formValues[AboutStepInputNames.Email]);
  React.useEffect(() => {
    if (emailTakenStatus) {
      if (!emailTakenStatus.exist) {
        setEmailTakenStatus?.(EmailTakenStatus.FREE);
      } else {
        if (emailTakenStatus.facebook) {
          setEmailTakenStatus?.(EmailTakenStatus.FACEBOOK);
        } else if (emailTakenStatus.google) {
          setEmailTakenStatus?.(EmailTakenStatus.GOOGLE);
        } else {
          setEmailTakenStatus?.(EmailTakenStatus.GIK);
        }
      }
    }
  }, [emailTakenStatus]);

  const aboutFormRef = React.useRef<FormType>(null);
  const situationFormRef = React.useRef<FormType>(null);
  const footerSituationCarouselRef = React.useRef<Slider>(null);
  const careCalendarFormRef = React.useRef<FormType>(null);
  const wishlistFormRef = React.useRef<FormType>(null);
  const donationsFormRef = React.useRef<FormType>(null);
  const donationsFormAsidePayPalRef = React.useRef<FormType>(null);
  const donationsFormAsideGoFundMeRef = React.useRef<FormType>(null);
  const privacyFormRef = React.useRef<FormType>(null);
  const pageFormRef = React.useRef<FormType>(null);
  const recipientFormRef = React.useRef<FormType>(null);
  const finishFormRef = React.useRef<FormType>(null);

  const [persistent, setPersistent, clearPersistent] = useLocalStorage<ICreateFlowFormValues>(bem());
  const [, , clearSelectedCategoryStorage] = useLocalStorage<string>(selectedCategoryStorageKey);
  const [stepIndexStorage, setStepIndexStorage, clearStepIndexStorage] = useLocalStorage<number>(stepIndexStorageKey);
  const [subStepIndexStorage, setSubStepIndexStorage, clearSubStepIndexStorage] = useLocalStorage<number>(
    subStepIndexStorageKey
  );
  const [open, setOpen] = React.useState<boolean>(false);

  function _setStepIndex(stepIndex: number) {
    setStepIndex(stepIndex);
    setStepIndexStorage(stepIndex);
  }

  function _setSubStepIndex(stepIndex: number) {
    setSubStepIndex(stepIndex);
    setSubStepIndexStorage(stepIndex);
  }

  function clearStorage() {
    clearSelectedCategoryStorage();
    clearStepIndexStorage();
    clearSubStepIndexStorage();
  }

  function openDialog() {
    setBlurred(true);
    setTimeout(() => {
      setOpen(true);
    }, 600);
  }

  useEffectOnce(() => {
    const waitForUITimeout = 500;
    (async () => {
      let finishAfterOauthRedirect = false;
      const urlParams = new URLSearchParams(window.location.search);
      const loginError = cookies.get(authCookieKeys.loginErrorCookieName);

      if (loginError) {
        await sleep(waitForUITimeout);
        await UI.alert(<OAuthErrorModalContent oauthErrorMessage={loginError} />, {
          okText: 'Try again',
          closable: true,
          title: 'Login',
          centeredTitle: false,
          className: bemBlock('auth-modal')(),
        });
        cookies.remove(authCookieKeys.loginErrorCookieName);
        urlParams.delete('finish');
        history?.replaceState(null, null, '?' + urlParams.toString());
      } else {
        if (urlParams.has('finish') && urlParams.get('finish') === 'true') {
          finishAfterOauthRedirect = true;

          if (urlParams.has('provider')) {
            const provider = urlParams.get('provider');
            console.log('PROVIDER GIVEN: ', provider);
            setNewUserLoginProvider(provider as 'facebook' | 'google');
          } else {
            console.log('PROVIDER MISSING :(');
          }
        }
      }

      let recipientType: string = null;
      if (
        urlParams.has('for') &&
        Object.values(RecipientType)
          .map(v => v.toString())
          .includes(urlParams.get('for'))
      ) {
        recipientType = urlParams.get('for');
      }

      if (finishAfterOauthRedirect) {
        setFormValues(persistent);
        setIsLoading(true);
      } else {
        if (persistent) {
          await sleep(waitForUITimeout);

          const continueFromPersistence = await UI.confirm(i18n.t(translationKeys.PersistenceDialogBody), {
            title: i18n.t(translationKeys.PersistenceDialogTitle),
            okText: i18n.t(translationKeys.PersistenceDialogOkText),
            cancelText: i18n.t(translationKeys.PersistenceDialogCancelText),
          });

          if (continueFromPersistence) {
            setFormValues(persistent);
            openDialog();

            let stepIndex = stepIndexStorage;
            if (isNaN(stepIndex)) {
              stepIndex = 0;
            }
            _setStepIndex(stepIndex);

            let subStepIndex = subStepIndexStorage;
            if (isNaN(subStepIndex)) {
              subStepIndex = 0;
            }
            _setSubStepIndex(subStepIndex);
          } else {
            clearPersistent();
            clearStorage();
            resetFormValues();
            openDialog();
          }
        } else {
          if (recipientType) {
            setFormValues({ ...formValues, recipientType } as ICreateFlowFormValues);
          }
          openDialog();
        }
      }
    })();
  });

  useEffect(() => {
    // execute the page creation once the page is loading, this ensures all form values have been submitted successfully
    if (isLoading) {
      (async () => {
        updateCreateProgress();
        let user: User;
        if (userStore.id) {
          user = await getUser(userStore.id);
        } else {
          try {
            user = await handleLoginSignup();
          } catch (e) {
            console.error('Create Flow: Failed to login/signup', e);
            Analytics.fireEvent(
              'CreatePageLoginSignupFailed',
              {},
              () => [],
              () => []
            );
            await UI.alert('There was an error completing your request. Please try again soon.');
          }
        }

        if (user) {
          updateCreateProgress();
          await trackOnFinish(user);
          try {
            // Note: creating the page will store a cookie to fire the Create_Page_GTM event on next load
            // see EventManager.js in legacy React project (will not be migrated)
            updateCreateProgress();
            const response = await createInkindPage(user);
            updateCreateProgress();
            await revalidateUserPages();
            updateCreateProgress();
            try {
              await AffiliateTracking.trackSignup(response.routeId);
              // conversion for page creations - use the page ID as both the external (unique) transaction ID, and the customer ID.
              await AffiliateTracking.trackConversion(response.routeId, response.routeId, 'pagecreation');
            } catch (error) {
              console.error('Failed to track affiliate conversion for inkind page creation', response.routeId, error);
            }
            updateCreateProgress();
            try {
              // FIXME: add gtag typings and extract
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (window as any)?.gtag?.('event', 'conversion', {
                send_to: 'AW-881191768/21VrCPiUu5YBENjWl6QD',
              });
            } catch (e) {
              console.error('Failed to send conversion to gtag', e);
            }
            updateCreateProgress();
            // TODO: after the SPA migration we will be able to fire this event immediately rather than after redirect.
            localStorage.setItem(createPageFinishedKey, response.routeId);
            await removeEmailUserTags({
              userEmail: user.emailAddress,
              tags: [emailUserTags.createPageReminders],
            });
            clearPersistent();
            clearStorage();
            updateCreateProgress();
            navigateTo(response.redirectUrl);
          } catch (e) {
            console.error('Create Flow: Failed to create new page', e);
            Analytics.fireEvent(
              'CreatePageFailed',
              {},
              () => [],
              () => []
            );
            await UI.alert('There was an error completing your request. Please try again soon.');
          }
        }
      })();
    }
    // FIXME: ensure this only occurs once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // prefetch it here so it's already available for the features step
  useFeatureVideos();

  function getFeaturesStepIndex(): number {
    return steps.findIndex(steps => steps.id === CreateFormSteps.FEATURES);
  }

  const pageStepIndex = steps.findIndex(steps => steps.id === CreateFormSteps.PAGE);

  function getCurrentFormsRef(stepIndex: number) {
    const featuresStepIndex = getFeaturesStepIndex();

    const forms = [
      aboutFormRef,
      situationFormRef,
      null, // page forms
      null, // featuresForms
      privacyFormRef,
      finishFormRef,
    ];

    const featuresForms = [careCalendarFormRef, wishlistFormRef, donationsFormRef];
    const featuresAsideForms = [[], [], [donationsFormAsidePayPalRef.current, donationsFormAsideGoFundMeRef.current]];
    const pageForms = [pageFormRef, recipientFormRef];

    if (stepIndex === featuresStepIndex) {
      return [featuresForms[subStepIndex]?.current, ...featuresAsideForms[subStepIndex]];
    } else if (stepIndex === pageStepIndex) {
      return [pageForms[subStepIndex]?.current];
    } else {
      return [forms[stepIndex]?.current];
    }
  }

  const hasNext = !(
    stepIndex === steps.length - 1 &&
    ((steps[stepIndex]?.subSteps && subStepIndex === steps[stepIndex]?.subSteps?.length - 1) ||
      !steps[stepIndex]?.subSteps)
  );

  function areFormsValid(forms: FormType[]): boolean {
    return forms?.every(form => (form?.api?.current ? form.api.current.getState().valid : true));
  }

  async function validateAndSubmitCurrentForm(
    step: number = stepIndex,
    options?: IFormSubmitAttemptOptions
  ): Promise<boolean> {
    const currentForms = getCurrentFormsRef(step);
    if (currentForms) {
      for (const form of currentForms) {
        if (form) {
          const formOk = await form?.submitAttempt?.(null, options);
          if (!formOk) {
            return false;
          }
        }
      }
    }
    return areFormsValid(currentForms);
  }

  function handleFormChange() {
    setNextDisabled(!areFormsValid(getCurrentFormsRef(stepIndex)));
  }

  useEffect(() => {
    // FIXME: without delay, form will be validated before it is rendered, causing incorrect result
    setTimeout(() => handleFormChange?.(), 1);
  }, [stepIndex, subStepIndex]);

  async function handleStepSubmit(values: ICreateFlowFormValues) {
    Object.keys(values).forEach(key => values[key] === undefined && delete values[key]);
    const newValues: ICreateFlowFormValues = { ...formValues, ...values };

    if (JSON.stringify(formValues).localeCompare(JSON.stringify(newValues)) !== 0) {
      setFormValues(newValues);
      setPersistent(newValues);
    }
  }

  async function handleSubmitOnChange(values: ICreateFlowFormValues) {
    handleFormChange();
    await handleStepSubmit(values);
  }

  async function handleClick(_step: StepItem<CreateFormSteps>, goToStepIndex: number, goToSubIndex: number) {
    _setStepIndex(goToStepIndex);
    _setSubStepIndex(goToSubIndex);
  }

  async function handleNext() {
    const currentForm = getCurrentFormsRef(stepIndex);
    let contextError = null;

    let notificationError: string;
    if (currentForm[0].id === CreatePageFormIds.CareCalendarStepForm && formValues.careCalendarEnabled === undefined) {
      notificationError = t(translationKeys.MissingFeatureSelection, { name: t(translationKeys.careCalendarTitle) });
    }
    if (currentForm[0].id === CreatePageFormIds.WishListStepForm && formValues.wishlistEnabled === undefined) {
      notificationError = t(translationKeys.MissingFeatureSelection, { name: t(translationKeys.WishlistTitle) });
    }
    if (currentForm[0].id === CreatePageFormIds.DonationsStepForm && formValues.donationsEnabled === undefined) {
      notificationError = t(translationKeys.MissingFeatureSelection, { name: t(translationKeys.FundraisingTitle) });
    }

    // the user must select either calendar or wishlist before they are allowed to continue
    if (
      currentForm[0].id === CreatePageFormIds.WishListStepForm &&
      formValues[FeatureWishlistInputNames.WishlistEnabled] !== true &&
      formValues[FeatureCareCalendarInputNames.CareCalendarEnabled] !== true
    ) {
      contextError = t(translationKeys.CantDisableCareCalendarAndWishlist);
      UI.notifyError(contextError);
    }

    const areFormsValid = await validateAndSubmitCurrentForm(stepIndex, {
      useValidationNotifications: !contextError,
      notificationError,
    });

    if (!areFormsValid || contextError) {
      return;
    }

    if (steps[stepIndex]?.subSteps && subStepIndex < steps[stepIndex].subSteps.length - 1) {
      _setSubStepIndex(subStepIndex + 1);
    } else {
      _setStepIndex(Math.min(stepIndex + 1, steps.length - 1));
      _setSubStepIndex(0);
    }
  }

  function handleBack() {
    if (steps[stepIndex]?.subSteps && subStepIndex > 0) {
      _setSubStepIndex(Math.max(subStepIndex - 1, 0));
    } else {
      _setStepIndex(Math.max(stepIndex - 1, 0));
      if (steps[stepIndex - 1]?.subSteps) {
        _setSubStepIndex(steps[stepIndex - 1].subSteps.length - 1);
      }
    }
  }

  async function handleCancel() {
    const response = await UI.confirm(i18n.t(translationKeys.CreatePageCancelConfirmation), {
      title: i18n.t(translationKeys.CreatePageCancelConfirmationTitle),
      okText: i18n.t(translationKeys.CreatePageCancelConfirmationOkButton),
      cancelText: i18n.t(translationKeys.CreatePageCancelConfirmationCancelButton),
    });

    if (response) {
      navigateTo('/');
    }
  }

  async function handleLoginSignup() {
    return new Promise<User>(resolve => {
      const unsub = userStoreApi.subscribe(
        (id: string) => {
          (async () => {
            const user = await getUser(id);
            resolve(user);
            unsub();
          })();
        },
        state => state.id
      );
    });
  }

  async function createInkindPage(user: User) {
    return await createInkind({
      pageTitle: formValues.pageName,
      situationIds: formValues.situations,
      alternateContact: {
        fullName: formValues.alternateNumberOwnerName,
        phoneNumber: formValues.alternateNumber,
      },
      enabledFeatures: {
        calendar: formValues.careCalendarEnabled,
        wishlist: formValues.wishlistEnabled,
        donations: formValues.donationsEnabled,
      },
      paypalEmail: formValues[FeatureDonationsInputNames.DonationsValues]?.[PayPalFormInputNames.EmailAddress],
      goFundMeUrl: extractCampaignSlug(
        formValues[FeatureDonationsInputNames.DonationsValues]?.[GoFundMeFormInputNames.PageURL]
      ),
      inviteRecipientAsOrganizer:
        formValues.recipientType == RecipientType.Myself
          ? true
          : formValues[RecipientInfoStepInputNames.AddAsPageOrganizer],
      privacySettings: {
        // TODO: consider renaming these to be consistent
        allowCrawlers: formValues.searchEnginesEnabled,
        allowFlowers: formValues.flowersEnabled,
        allowPageSharing: formValues.pageSharingEnabled,
        allowPhoneCalls: formValues.phoneCallsEnabled,
        allowTexts: formValues.textMessagesEnabled,
        allowVisitors: formValues.visitorsEnabled,
        canFeature: formValues.featurePageEnabled,
        isPublic: formValues.showPageEnabled,
      },
      recipient: {
        type: formValues.recipientType,
        fullName:
          formValues.recipientType == RecipientType.Myself
            ? user.fullName
            : formValues[RecipientInfoStepInputNames.RecipientName],
        email:
          formValues.recipientType == RecipientType.Myself
            ? user.emailAddress
            : formValues[RecipientInfoStepInputNames.RecipientEmail],
        address1: formValues.address1,
        address2: formValues.address2,
        city: formValues.city,
        state: formValues.state,
        postalCode: formValues.postalCode,
      },
      selectedWishlistProductIds: formValues.wishlistItems,
    });
  }

  async function startPageCreation() {
    setIsLoading(true);
  }

  async function handleFinish() {
    if (await validateAndSubmitCurrentForm()) {
      // only start page creation if we are already logged in
      // otherwise, we will wait for the onSuccess of the finish step to fire.
      if (userStore.id) {
        startPageCreation();
      }
    }
  }

  // TODO: review how drawers work - would be nice to be able to launch drawers through the UI manager.
  // the reason why they need to be portalled into the root of the modal is so that the drawer is in the
  // same stacking context as the modal header and footer.
  const drawerPortal = document.getElementsByClassName(bem())?.[0] as HTMLElement;

  const isDataLoading = !situations;
  const isError = isDataLoading && situations;

  if (isLoading) {
    return <CreatePageFallback />;
  }
  if (isError) {
    return <CreatePageFallback />;
  }

  return (
    <CreatePageContext.Provider
      value={{
        aboutFormRef,
        situationFormRef,
        footerSituationCarouselRef,
        careCalendarFormRef,
        wishlistFormRef,
        donationsFormRef,
        donationsFormAsidePayPalRef,
        donationsFormAsideGoFundMeRef,
        privacyFormRef,
        pageFormRef,
        recipientFormRef,
        finishFormRef,
        handleBack,
        handleCancel,
        handleNext,
        handleFinish,
      }}
    >
      {isBrowser() && <ThumbnailPreFetcher />}
      <InKindPageBackground blurred={blurred} />
      {isLoading && (
        <CenterFixed>
          <LoadingSpinner variant={isMd ? 'primary' : 'default'} />
        </CenterFixed>
      )}
      {!isLoading && (
        <Modal
          allowIntercom
          bodyOpenClassName={bem('body-modal-open')}
          className={bem(null, [{ [`step-${steps[stepIndex]?.id}`]: true }, { [`substep-${subStepIndex}`]: true }])}
          isOpen={open}
          onClose={handleCancel}
          pages={[
            {
              title: i18n.t(translationKeys.CreateTitle),
              pageArrows: stepIndex === 1,
              pageArrowNextDisabled: selectedCategoryIndex >= situationCategories?.length - 1,
              pageArrowPrevDisabled: selectedCategoryIndex === 0,
              onArrowNext: () => {
                situationCarouselRef.slickNext();
              },
              onArrowPrev: () => {
                situationCarouselRef.slickPrev();
              },
              content: (
                <>
                  <Steps
                    className={bem('steps')}
                    allowBackwardNavigation
                    allowForwardNavigation={false}
                    index={stepIndex}
                    subIndex={subStepIndex}
                    onClick={handleClick}
                    steps={steps}
                  >
                    <AboutStep
                      initialValues={formValues}
                      onSubmit={handleStepSubmit}
                      onChange={handleFormChange}
                      trackTermsOfService={trackTermsOfService}
                      trackPrivacyPolicy={trackPrivacyPolicy}
                    />

                    <SituationStep initialValues={formValues} onSubmit={handleStepSubmit} onChange={handleFormChange} />

                    {formValues[AboutStepInputNames.RecipientType] !== RecipientType.Myself ? (
                      <SubStep>
                        <NameYourPageStep
                          onSubmit={handleStepSubmit}
                          onChange={handleFormChange}
                          trackClickedPageNameIdeas={trackClickedPageNameIdeas}
                        />

                        <RecipientInfoStep onSubmit={handleStepSubmit} onChange={handleFormChange} />
                      </SubStep>
                    ) : (
                      <NameYourPageStep
                        onSubmit={handleStepSubmit}
                        onChange={handleFormChange}
                        trackClickedPageNameIdeas={trackClickedPageNameIdeas}
                      />
                    )}

                    <SubStep>
                      <CreatePageSplitLayout className={bem('features-split-layout')}>
                        <main>
                          <CareCalendarStep
                            id={CreatePageFormIds.CareCalendarStepForm}
                            initialValues={formValues}
                            onSubmit={handleStepSubmit}
                            onChange={handleSubmitOnChange}
                          />
                        </main>
                        <FeaturesAside
                          onChange={handleSubmitOnChange}
                          trackCalendarVideoPlayed={trackCalendarVideoPlayed}
                        />
                      </CreatePageSplitLayout>

                      <CreatePageSplitLayout className={bem('features-split-layout')}>
                        <main>
                          <WishlistStep
                            id={CreatePageFormIds.WishListStepForm}
                            initialValues={formValues}
                            onSubmit={handleStepSubmit}
                            onChange={handleSubmitOnChange}
                          />
                        </main>
                        <FeaturesAside
                          onChange={handleSubmitOnChange}
                          trackWishlistVideoPlayed={trackWishlistVideoPlayed}
                        />
                      </CreatePageSplitLayout>

                      <CreatePageSplitLayout className={bem('features-split-layout')}>
                        <main>
                          <DonationsStep
                            id={CreatePageFormIds.DonationsStepForm}
                            initialValues={formValues}
                            onSubmit={handleStepSubmit}
                            onChange={handleSubmitOnChange}
                          />
                        </main>
                        <FeaturesAside
                          onChange={handleSubmitOnChange}
                          trackDonationsVideoPlayed={trackDonationsVideoPlayed}
                        />
                      </CreatePageSplitLayout>
                    </SubStep>

                    <PrivacyStep onChange={handleSubmitOnChange} />

                    <FinishStep onSubmit={handleStepSubmit} onSuccess={startPageCreation} />
                  </Steps>
                  <Drawer
                    portal={drawerPortal}
                    placement="bottom"
                    variant="neutral"
                    shadow
                    isOpen={isDrawerOpen}
                    onClose={() => closeLegalTextDrawer()}
                    className={drawerBem(null, null, 'gik-modal-drawer')}
                    padded
                    title={isDrawerLoading ? <>&nbsp;</> : <HTMLParser rawHtml={legalTextDrawerTitle} />}
                  >
                    {isDrawerLoading ? (
                      <div className={drawerBem('loading-container')}>
                        <LoadingSpinner center />
                      </div>
                    ) : (
                      /* TODO: UI Article component */
                      <div className={drawerBem('copy', undefined, ['gik-article'])}>
                        <HTMLParser rawHtml={legalTextDrawerCopy} />
                      </div>
                    )}
                  </Drawer>
                  <Drawer
                    portal={drawerPortal}
                    placement="bottom"
                    variant="neutral"
                    shadow
                    closeBtn={false}
                    isOpen={isPageNameDrawerOpen}
                    onClose={() => closePageNameDrawer()}
                    className={drawerBem(null, null, 'gik-modal-drawer')}
                    padded
                    footer={({ close }) => <Button onClick={close}>Close</Button>}
                  >
                    <div className={pageNameBem()}>
                      <span className={pageNameBem('title')}>
                        Here are some ideas for Page Names to help you get started:
                      </span>
                      <PageNameIdeas
                        situationIds={formValues.situations}
                        onSelect={(name: string) => {
                          closePageNameDrawer();
                          pageFormRef.current.api.current?.mutators.setPageName(name);
                        }}
                      />
                    </div>
                  </Drawer>
                </>
              ),
            },
          ]}
          footer={
            <CreatePageNavigation
              className={bem('navigation')}
              stepIndex={stepIndex}
              hasNext={hasNext}
              trackIntercomClicked={trackIntercomClicked}
            />
          }
        />
      )}
    </CreatePageContext.Provider>
  );
}, CreatePageFallback);

// loads gift card and situations thumbnails in advance providing a *much* smoother experience :)
function ThumbnailPreFetcher() {
  const suggestedProducts = useCreateFlowStore(state => state.suggestedProducts);
  const situations = useCreateFlowStore(state => state.situations);
  const bem = bemBlock(blockName);

  return (
    <div className={bem('thumbnail-prefetcher')}>
      {suggestedProducts?.map((product, i) => (
        <img key={i} alt="prefetch" src={product?.gridImage} />
      ))}
      {situations?.map((situation, i) => (
        <img key={i} alt="prefetch" src={situation?.acf?.taxonomy_svg_icon} />
      ))}
    </div>
  );
}
